import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PurchaseRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchPurchases, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney, formatPosDate, parseAmount } from '@/lib/format';

const FILTER_FIELDS = ['Purchase', 'Supplier', 'Invoice', 'Payment'] as const;

const TOGGLEABLE = ['branch', 'invoice', 'payment', 'subtotal', 'tax', 'discount', 'total'] as const;

const COLUMN_LABELS: Record<string, string> = {
  branch: 'Branch',
  invoice: 'Invoice',
  payment: 'Payment',
  subtotal: 'Subtotal',
  tax: 'VAT',
  discount: 'Discount',
  total: 'Total',
};

export function PurchasesPage() {
  const { currency, filters, multiBranch } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('purchases', TOGGLEABLE);

  const purchasesQuery = useQuery({
    queryKey: portalKeys.purchases(range),
    queryFn: ({ signal }) => fetchPurchases(range, signal),
  });

  const report = purchasesQuery.data;
  const reportCurrency = report?.currency || currency;

  const rows = useMemo(
    () =>
      applyClientFilters(report?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Purchase'
            ? row.purchaseNumber
            : field === 'Supplier'
              ? row.supplierName
              : field === 'Invoice'
                ? row.invoiceNumber
                : field === 'Payment'
                  ? row.paymentMethod
                  : undefined,
        searchValues: (row) => [row.purchaseNumber, row.supplierName, row.invoiceNumber],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const baseColumns = useMemo<Array<Column<PurchaseRow>>>(() => {
    const money = (value: string) => formatMoney(value, reportCurrency);
    const dashIfZero = (value: string) =>
      parseAmount(value) === 0 ? <span className="text-slate-300">—</span> : money(value);

    return [
      {
        key: 'purchaseNumber',
        header: 'Purchase',
        pinned: true,
        cell: (row) => (
          <div>
            <p className="font-medium text-slate-900">{row.purchaseNumber}</p>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">
              {formatPosDate(row.date)}
            </p>
          </div>
        ),
      },
      {
        key: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-slate-500">{row.branchName || '—'}</span>,
      },
      {
        key: 'supplier',
        header: 'Supplier',
        pinned: true,
        cell: (row) => <span className="text-slate-700">{row.supplierName || '—'}</span>,
      },
      {
        key: 'invoice',
        header: 'Invoice',
        cell: (row) => <span className="text-slate-500">{row.invoiceNumber || '—'}</span>,
      },
      {
        key: 'payment',
        header: 'Payment',
        cell: (row) => <span className="text-slate-500">{row.paymentMethod || '—'}</span>,
      },
      {
        key: 'subtotal',
        header: 'Subtotal',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.subtotal),
      },
      {
        key: 'tax',
        header: 'VAT',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => dashIfZero(row.taxAmount),
      },
      {
        key: 'discount',
        header: 'Discount',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => dashIfZero(row.discountAmount),
      },
      {
        key: 'total',
        header: 'Total',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => money(row.total),
      },
    ];
  }, [reportCurrency]);

  const columns = useMemo(() => {
    const resolved = applyReportLayout(baseColumns, layout);
    return multiBranch ? resolved : resolved.filter((column) => column.key !== 'branch');
  }, [baseColumns, layout, multiBranch]);

  const header = (
    <PageHeader
      title="Purchase list"
      description="Stock and supplies bought in the selected range."
      actions={
        <ExportMenu
          report="purchases"
          range={range}
          // Nothing to export until the report has loaded, and an
          // errored report would produce an empty file.
          disabled={purchasesQuery.isPending || purchasesQuery.isError}
        />
      }
    />
  );

  if (purchasesQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={purchasesQuery.error}
            onRetry={() => void purchasesQuery.refetch()}
            retrying={purchasesQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={purchasesQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 3 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile
                label="Purchased"
                value={formatMoney(data.totalPurchased, data.currency || currency)}
              />
              <KpiTile
                label="VAT"
                value={formatMoney(data.totalTax, data.currency || currency)}
              />
              <KpiTile label="Purchases" value={formatCount(data.count)} />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard
          headingId="purchases-table"
          title="Purchases"
          count={rows.length}
          description="Newest first. Scroll sideways for the full breakdown."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Purchase, supplier or invoice"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={purchasesQuery}
            skeleton={<TableSkeleton rows={6} columns={7} />}
            isEmpty={() => rows.length === 0}
            emptyTitle={toolbar.search ? `No purchases match “${toolbar.search}”` : 'No purchases in this range'}
            emptyDescription="Pick a wider date range, or check another branch or till."
          >
            {() => (
              <DataTable
                caption="Purchases with supplier, invoice, payment method, VAT and total"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                layout={layout}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
